import { useState, useEffect, useCallback } from 'react';

export interface CartItem {
  productId: string;
  productName: string;
  productImage: string;
  sku: string;
  quantity: number;
  moq: number;
  moqUnit: string;
  material?: string;
  capacity?: string;
  categoryL1?: string;
  categoryL2?: string;
}

const CART_STORAGE_KEY = 'gbcospack_cart';

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

function saveCart(items: CartItem[]) {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch {
    // storage full or unavailable - silently ignore
  }
}

let cartItems: CartItem[] = loadCart();
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((fn) => fn());
}

export function getCartItems(): CartItem[] {
  return cartItems;
}

export function getCartCount(): number {
  return cartItems.length;
}

export function addToCart(item: CartItem) {
  const existing = cartItems.find((i) => i.productId === item.productId);
  if (existing) {
    existing.quantity += item.quantity;
  } else {
    cartItems.push({ ...item });
  }
  saveCart(cartItems);
  notifyListeners();
}

export function removeFromCart(productId: string) {
  cartItems = cartItems.filter((i) => i.productId !== productId);
  saveCart(cartItems);
  notifyListeners();
}

export function updateCartQuantity(productId: string, quantity: number) {
  const item = cartItems.find((i) => i.productId === productId);
  if (item) {
    item.quantity = Math.max(1, quantity);
  }
  saveCart(cartItems);
  notifyListeners();
}

export function clearCart() {
  cartItems = [];
  saveCart(cartItems);
  notifyListeners();
}

export function useCart() {
  const [, setTick] = useState(0);

  useEffect(() => {
    const listener = () => setTick((t) => t + 1);
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, []);

  const items = cartItems;
  const count = getCartCount();

  const addItem = useCallback((item: CartItem) => {
    addToCart(item);
  }, []);

  const removeItem = useCallback((productId: string) => {
    removeFromCart(productId);
  }, []);

  const updateQuantity = useCallback((productId: string, quantity: number) => {
    updateCartQuantity(productId, quantity);
  }, []);

  const emptyCart = useCallback(() => {
    clearCart();
  }, []);

  return {
    items,
    count,
    addItem,
    removeItem,
    updateQuantity,
    emptyCart,
  };
}